/**
 * EdgeStreamApiClient - REST API client for EdgeStream backend endpoints.
 *
 * Framework-agnostic (pure fetch). Can be used from React, Node, CLI, etc.
 */

import { createModuleLogger } from '../utils/logger';

const logger = createModuleLogger('EdgeStreamApiClient');

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ServerInfo {
  serverId: string;
  serverName: string;
  status: 'connected' | 'disconnected' | 'error';
  messageCount: number;
  lastEventAt?: string;
}

export interface GroupInfo {
  groupId: string;
  groupName: string;
  description?: string;
  serverId: string;
  memberCount: number;
  createdAt: string;
  createdBy?: string;
  lastActivityAt?: string;
  members?: string[];
}

export interface CreateGroupRequest {
  data: {
    serverId: string;
    groupName: string;
    description?: string;
    createdBy: string;
  };
}

export interface ServerMetadata {
  serverId: string;
  serverName: string;
  serverDescription?: string;
  updatedAt: string;
}

export interface SetServerMetadataRequest {
  data: {
    serverId: string;
    serverName: string;
    serverDescription?: string;
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Normalise backend responses that may be a bare array or `{ data: [...] }` or single object. */
function normalizeArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && 'data' in data) {
    const inner = (data as Record<string, unknown>).data;
    if (Array.isArray(inner)) return inner as T[];
    // If data.data is a single object, wrap it in an array
    if (inner && typeof inner === 'object') return [inner as T];
  }
  logger.debug('Expected array response, got unexpected shape - returning empty array');
  return [];
}

/** Extract `.data` from a wrapped response, or return the value itself. */
function unwrap<T>(data: unknown): T | null {
  if (data && typeof data === 'object' && 'data' in data) {
    return (data as Record<string, unknown>).data as T;
  }
  return data as T;
}

// ─── Client ──────────────────────────────────────────────────────────────────

export class EdgeStreamApiClient {
  constructor(
    private readonly baseUrl: string,
    private readonly getToken?: () => string | null,
  ) {}

  private authHeaders(): Record<string, string> {
    const token = this.getToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // ── Servers ──────────────────────────────────────────────────────────────

  async getServers(): Promise<ServerInfo[]> {
    const response = await fetch(`${this.baseUrl}/api/edge-stream/servers`, { headers: this.authHeaders() });
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    const data = await response.json();
    logger.debug('Servers response received');
    return normalizeArray<ServerInfo>(data);
  }

  async getServerDetails(serverId: string): Promise<ServerInfo | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/edge-stream/servers/${serverId}`, { headers: this.authHeaders() });
      const data = await response.json();
      return unwrap<ServerInfo>(data);
    } catch (error) {
      logger.error('Error fetching server details', error instanceof Error ? error : undefined);
      return null;
    }
  }

  // ── Groups ───────────────────────────────────────────────────────────────

  async getGroupsByServer(serverId: string): Promise<GroupInfo[]> {
    const response = await fetch(`${this.baseUrl}/api/edge-stream/groups/${serverId}`, { headers: this.authHeaders() });
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    const data = await response.json();
    logger.debug(`Groups response received for ${serverId}`);
    return normalizeArray<GroupInfo>(data);
  }

  async getGroupDetails(serverId: string, groupName: string): Promise<GroupInfo | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/edge-stream/groups/${serverId}/${groupName}`, { headers: this.authHeaders() });
      const data = await response.json();
      return unwrap<GroupInfo>(data);
    } catch (error) {
      logger.error('Error fetching group details', error instanceof Error ? error : undefined);
      return null;
    }
  }

  async createGroup(serverId: string, groupName: string, description: string, createdBy: string): Promise<GroupInfo | null> {
    try {
      const request: CreateGroupRequest = {
        data: { serverId, groupName, description, createdBy },
      };
      const response = await fetch(`${this.baseUrl}/api/edge-stream/groups/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(request),
      });
      const data = await response.json();
      return unwrap<GroupInfo>(data);
    } catch (error) {
      logger.error('Error creating group', error instanceof Error ? error : undefined);
      return null;
    }
  }

  async joinGroup(serverId: string, groupName: string, userId?: string): Promise<boolean> {
    try {
      const url = userId
        ? `${this.baseUrl}/api/edge-stream/groups/${serverId}/${groupName}/join?userId=${encodeURIComponent(userId)}`
        : `${this.baseUrl}/api/edge-stream/groups/${serverId}/${groupName}/join`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
      });
      const data = await response.json();
      return (data as Record<string, unknown>).success === true;
    } catch (error) {
      logger.error('Error joining group', error instanceof Error ? error : undefined);
      return false;
    }
  }

  async leaveGroup(serverId: string, groupName: string, userId?: string): Promise<boolean> {
    try {
      const url = userId
        ? `${this.baseUrl}/api/edge-stream/groups/${serverId}/${groupName}/leave?userId=${encodeURIComponent(userId)}`
        : `${this.baseUrl}/api/edge-stream/groups/${serverId}/${groupName}/leave`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
      });
      const data = await response.json();
      return (data as Record<string, unknown>).success === true;
    } catch (error) {
      logger.error('Error leaving group', error instanceof Error ? error : undefined);
      return false;
    }
  }

  async getUserGroups(connectionId: string): Promise<GroupInfo[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/edge-stream/groups/user/${connectionId}`, { headers: this.authHeaders() });
      const data = await response.json();
      return unwrap<GroupInfo[]>(data) ?? [];
    } catch (error) {
      logger.error('Error fetching user groups', error instanceof Error ? error : undefined);
      return [];
    }
  }

  // ── Connections ──────────────────────────────────────────────────────────

  async getConnectionStatus(): Promise<{ activeConnections: number }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/edge-stream/connections/status`, { headers: this.authHeaders() });
      return await response.json();
    } catch (error) {
      logger.error('Error fetching connection status', error instanceof Error ? error : undefined);
      return { activeConnections: 0 };
    }
  }

  // ── Events ───────────────────────────────────────────────────────────────

  async sendEvent(serverId: string, topic: string, body: unknown, protocol: string = 'JSON'): Promise<boolean> {
    try {
      const payload = { data: { serverId, topic, body, protocol } };
      logger.debug('Sending event');
      const response = await fetch(`${this.baseUrl}/api/edge-stream/events/send-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      return (data as Record<string, unknown>).success === true;
    } catch (error) {
      logger.error('Error sending event', error instanceof Error ? error : undefined);
      return false;
    }
  }

  async sendChatMessage(serverId: string, author: string, text: string, groupName?: string): Promise<boolean> {
    try {
      const payload = { data: { serverId, author, message: text, groupName } };
      logger.debug('Sending chat message');
      const response = await fetch(`${this.baseUrl}/api/edge-stream/events/send-chat-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      return (data as Record<string, unknown>).success === true;
    } catch (error) {
      logger.error('Error sending chat message', error instanceof Error ? error : undefined);
      return false;
    }
  }

  // ── Server Metadata ──────────────────────────────────────────────────────

  async getAllServerMetadata(): Promise<ServerMetadata[]> {
    const response = await fetch(`${this.baseUrl}/api/edge-stream/servers/metadata`, { headers: this.authHeaders() });
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    const data = await response.json();
    logger.debug('Server metadata response received');
    return normalizeArray<ServerMetadata>(data);
  }

  async getServerMetadata(serverId: string): Promise<ServerMetadata | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/edge-stream/servers/metadata/${serverId}`, { headers: this.authHeaders() });
      const data = await response.json();
      return unwrap<ServerMetadata>(data);
    } catch (error) {
      logger.error('Error fetching server metadata', error instanceof Error ? error : undefined);
      return null;
    }
  }

  async setServerMetadata(serverId: string, serverName: string, serverDescription?: string): Promise<ServerMetadata | null> {
    try {
      const request: SetServerMetadataRequest = {
        data: { serverId, serverName, serverDescription },
      };
      const response = await fetch(`${this.baseUrl}/api/edge-stream/servers/metadata`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(request),
      });
      const data = await response.json();
      return unwrap<ServerMetadata>(data);
    } catch (error) {
      logger.error('Error setting server metadata', error instanceof Error ? error : undefined);
      return null;
    }
  }
}

/** Factory function to create an API client instance. */
export function createAPI(baseUrl: string, getToken?: () => string | null): EdgeStreamApiClient {
  return new EdgeStreamApiClient(baseUrl, getToken);
}
